import { z } from "@hono/zod-openapi";

// Helper schemas for user fields
export const ReservedRoomSchema = z.object({
  id: z.string().min(3).max(20),
  label: z.string().max(50).optional(),
  password: z.string().optional(),
});

// Input schema for creating a reserved room (id comes as roomId from client)
export const CreateReservedRoomInput = z.object({
  roomId: z.string().min(3, "Room ID must be at least 3 characters").max(
    20,
    "Room ID must be at most 20 characters",
  ),
  label: z.string().max(50, "Label must be at most 50 characters").optional(),
  password: z.string().optional(),
  isPublic: z.boolean().optional(),
  geo: z.record(z.string(), z.unknown()).optional(),
  quiz: z.record(z.string(), z.unknown()).optional(),
});

const BanEntrySchema = z.object({
  ip: z.string().optional(),
  userId: z.string().optional(),
  reason: z.string().optional(),
  createdAt: z.number().optional(),
});

// User Model
export const UserModel = z.object({
  email: z.string().email(),
  emailVerified: z.boolean().default(false),
  name: z.string().optional(),
  image: z.string().nullable().optional(),
  color: z.string().max(9).optional(),
  frame: z.enum(["circle", "square"]).optional(),
  // Additional fields
  theme: z.string().default("forest"),
  reservedRooms: z.array(z.string()).default([]),
  bannedIps: z.array(BanEntrySchema).default([]),
  bannedUsers: z.array(BanEntrySchema).default([]),
  isAnonymous: z.boolean().default(false),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type User = z.infer<typeof UserModel>;

// Session Model
export const SessionModel = z.object({
  userId: z.string(),
  token: z.string(),
  expiresAt: z.date(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  // Additional fields
  theme: z.string().default("forest"),
  flash: z.string().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Session = z.infer<typeof SessionModel>;

// Account Model (for OAuth providers)
export const AccountModel = z.object({
  userId: z.string(),
  providerId: z.string(),
  accountId: z.string(),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  accessTokenExpiresAt: z.date().optional(),
  refreshTokenExpiresAt: z.date().optional(),
  scope: z.string().optional(),
  password: z.string().optional(), // For email/password provider
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Account = z.infer<typeof AccountModel>;

// Verification Model (for email verification, password reset, etc.)
export const VerificationModel = z.object({
  identifier: z.string(),
  value: z.string(),
  expiresAt: z.date(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Verification = z.infer<typeof VerificationModel>;

// API Token Model (for programmatic API access)
export const ApiTokenModel = z.object({
  userId: z.string(),
  name: z.string().max(100),
  tokenHash: z.string(), // SHA-256 hash of the actual token
  expiresAt: z.date().nullable(), // null = never expires
  lastUsedAt: z.number().nullable(),
  createdAt: z.number(),
});
export type ApiToken = z.infer<typeof ApiTokenModel>;

// Input schema for creating an API token
export const CreateApiTokenInput = z.object({
  name: z.string().min(1, "Token name is required").max(
    100,
    "Token name must be 100 characters or less",
  ),
  expiresAt: z.string().nullable().optional(), // ISO date string, optional
});
